import { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import { IPedido } from "../../interface/IPedido";
import { StatusPedido, StatusPedidoDescricao } from "../../enum/StatusPedido";
import { TipoPedido } from "../../enum/TipoPedido";
import { TipoMarketplace } from "../../../marketplace/enum/TipoMarketplace";
import { usePedidos } from "../../hooks/usePedidos";
import { useEntregadores } from "../../../entregadores/hooks/useEntregadores";
import { IEntregador } from "../../../entregadores/interfaces/IEntregador";
import { ModalCancelarPedido } from "../../pages/ListagemPedidos/containers/FerramentasPedidos/components/ModalCancelarPedido";
import { useNavigate } from "react-router-dom";

interface AtualizarStatusPedidoProps {
    pedido: IPedido;
    onStatusAtualizado: () => void;
    zIndex?: number;
}

const statusTerminais = [
    StatusPedido.Cancelado,
    StatusPedido["Concluído"],
    StatusPedido["Em negociação"],
    StatusPedido["Aguardando resposta cliente"],
];

export const AtualizarStatusPedido = ({ pedido, onStatusAtualizado, zIndex }: AtualizarStatusPedidoProps) => {
    const { atualizarStatus, loading } = usePedidos();
    const { listar, loading: loadingEntregadores } = useEntregadores();
    const navigate = useNavigate();

    const [cancelarOpen, setCancelarOpen] = useState(false);
    const [despacharOpen, setDespacharOpen] = useState(false);
    const [entregadores, setEntregadores] = useState<IEntregador[]>([]);
    const [entregadorSelecionado, setEntregadorSelecionado] = useState("");

    const isDeliveryEmPreparo =
        pedido.status === StatusPedido["Em preparo"] && pedido.tipo === TipoPedido.delivery;

    useEffect(() => {
        if (isDeliveryEmPreparo) {
            listar().then(setEntregadores);
        }
    }, [pedido.uuid, pedido.status]);

    if (statusTerminais.includes(pedido.status as StatusPedido)) {
        return null;
    }

    const handleAtualizarStatus = async (status: StatusPedidoDescricao, entregador?: IEntregador) => {
        await atualizarStatus({ pedidoUuid: pedido.uuid, status, entregador });
        onStatusAtualizado();
    };

    const handleConfirmarDespacho = async () => {
        const entregador = entregadores.find((e) => e.uuid === entregadorSelecionado);
        await handleAtualizarStatus(StatusPedidoDescricao["Em transporte"], entregador);
        setDespacharOpen(false);
    };

    const renderBotoes = () => {
        const { status, tipo, marketplace } = pedido;
        const isIfood = marketplace?.tipo === TipoMarketplace.IFOOD;

        if (status === StatusPedido["Aguardando confirmação"] || status === StatusPedido["Aguardando pagamento"]) {
            return (
                <>
                    <Button size="small" variant="outlined" onClick={() => setCancelarOpen(true)}>
                        Cancelar
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAtualizarStatus(StatusPedidoDescricao["Em preparo"])}
                    >
                        Confirmar pedido
                    </Button>
                </>
            );
        }

        if (status === StatusPedido["Em andamento"]) {
            return (
                <>
                    <Button size="small" variant="outlined" onClick={() => setCancelarOpen(true)}>
                        Cancelar
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAtualizarStatus(StatusPedidoDescricao["Concluído"])}
                    >
                        Concluir pedido
                    </Button>
                </>
            );
        }

        if (status === StatusPedido["Em preparo"] && tipo === TipoPedido.delivery) {
            return (
                <>
                    <Button size="small" variant="outlined" onClick={() => setCancelarOpen(true)}>
                        Cancelar
                    </Button>
                    <Button size="small" variant="contained" onClick={() => setDespacharOpen(true)}>
                        Despachar pedido
                    </Button>
                </>
            );
        }

        if (status === StatusPedido["Em preparo"] && tipo === TipoPedido.retirada) {
            return (
                <>
                    <Button size="small" variant="outlined" onClick={() => setCancelarOpen(true)}>
                        Cancelar
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAtualizarStatus(StatusPedidoDescricao["Pronto para retirada"])}
                    >
                        Pronto para coleta
                    </Button>
                </>
            );
        }

        if (status === StatusPedido["Em transporte"] && tipo === TipoPedido.delivery) {
            return (
                <>
                    <Button size="small" variant="outlined" onClick={() => setCancelarOpen(true)}>
                        Cancelar
                    </Button>
                    {!isIfood && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleAtualizarStatus(StatusPedidoDescricao["Concluído"])}
                        >
                            Concluir pedido
                        </Button>
                    )}
                </>
            );
        }

        if (status === StatusPedido["Pronto para retirada"] && tipo === TipoPedido.retirada) {
            return (
                <>
                    <Button size="small" variant="outlined" onClick={() => setCancelarOpen(true)}>
                        Cancelar
                    </Button>
                    {!isIfood && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleAtualizarStatus(StatusPedidoDescricao["Concluído"])}
                        >
                            Concluir pedido
                        </Button>
                    )}
                </>
            );
        }

        return null;
    };

    const botoes = renderBotoes();
    if (!botoes) return null;

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {loading ? <CircularProgress size={24} /> : botoes}
            </Box>

            <ModalCancelarPedido
                open={cancelarOpen}
                fechar={() => setCancelarOpen(false)}
                atualizarStatus={async (props) => {
                    await atualizarStatus(props);
                    onStatusAtualizado();
                }}
                loading={loading}
                pedido={pedido}
                zIndex={zIndex}
            />

            <Dialog open={despacharOpen} onClose={() => setDespacharOpen(false)} sx={zIndex ? { zIndex } : undefined}>
                <DialogTitle>Selecionar entregador</DialogTitle>
                <DialogContent sx={{ minWidth: "400px" }}>
                    {loadingEntregadores ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : entregadores.length ? (
                        <Select
                            fullWidth
                            value={entregadorSelecionado}
                            onChange={(e) => setEntregadorSelecionado(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>
                                Selecione um entregador
                            </MenuItem>
                            {entregadores.map((entregador) => (
                                <MenuItem key={entregador.uuid} value={entregador.uuid}>
                                    {entregador.nome} - {entregador.telefone}
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            <Typography>Nenhum entregador cadastrado</Typography>
                            <Button onClick={() => navigate("/entregadores")}>
                                Cadastrar entregador
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDespacharOpen(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmarDespacho} variant="contained">
                        {!entregadorSelecionado ? "Continuar sem entregador" : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
